// Landscape utils
// --------------------------------------------------------
import * as Sentry from '@sentry/vue'

function reportBrokenRef(what, id, where, ref) {
  const e = new Error(`${what}: ${id} broken ref in ${where}: ${ref}`)
  e.name = `${what} broken actor reference`
  Sentry.captureException(e)
}
let utils = {}
// Add extra properties to original Topics
utils.parseTopics = (topics, context) =>
  topics.forEach(topic => parseTopic(topic, context))
const parseTopic = (topic, context) => {
  const actor = context.getters.getActorByID(topic.actorId)
  if (!actor._id) {
    reportBrokenRef('topic', topic._id, 'actorId', topic.actorId)
  }
  topic.color = actor.color
  topic.currentUser.favorited = !!topic.currentUser.favorited // make sure it's boolean
  // for sorting
  if (!topic.lastComment) {
    topic.lastComment = '1981-11-26T00:00:00.000Z' // very old
  }
  topic.lastActivityAt = topic.updatedAt
  if (topic.lastComment > topic.updatedAt) {
    topic.lastActivityAt = topic.lastComment
  }
  topic.prioritySorter = topic.priority ? 1 : 0
  topic.breadcrumb = topic.parentsId.map(id => {
    const actor = context.getters.getActorByID(id)
    if (!actor._id) {
      reportBrokenRef('topic', topic._id, 'parentsId', id)
    }
    return {
      name: actor.name,
      pathArr: actor.pathArr
    }
  })
}

// Add extra properties to original Feeds
utils.parseFeeds = (feeds, sources, getters) =>
  feeds.forEach(feed => parseFeeds(feed, sources, getters))
const parseFeeds = (feed, sources, getters) => {
  feed.colors = []
  feed.breadcrumbs = []
  feed.source.forEach(sourceId => {
    const source = sources[sourceId]
    if (!source) return // break on error
    const actorId = source.actorId
    feed.actorId = actorId
    const actor = getters.getActorByID(actorId)
    if (!actor._id) {
      reportBrokenRef('source', sourceId, 'actorId', actorId)
    }
    feed.colors.push(actor.color)
    const ancestors = actor.ancestors || []
    const bc = ancestors.map(ancestorId => {
      const ancestor = getters.getActorByID(ancestorId)
      return {
        name: ancestor.name,
        pathArr: ancestor.pathArr
      }
    })
    feed.breadcrumbs.push(bc)
  })
}

// Add extra properties to original Feed Sources
utils.parseFeedSources = (sources, getters) =>
  sources.map(source => {
    const actor = getters.getActorByID(source.actorId)
    if (!actor._id) {
      reportBrokenRef('source', source._id, 'actorId', source.actorId)
    }
    source.colors = []
    source.breadcrumbs = []
    source.colors.push(actor.color)
    const ancestors = actor.ancestors || []
    const bc = ancestors.map(ancestorId => {
      const ancestor = getters.getActorByID(ancestorId)
      return {
        name: ancestor.name,
        pathArr: ancestor.pathArr
      }
    })
    source.breadcrumbs.push(bc)
  })

utils.parseAxes = (axes, insights) => {
  let dictionary = {}
  for (let axis of axes) {
    recursiveParseAxes(dictionary, axis, insights)
  }
  return dictionary
}

const recursiveParseAxes = (dictionary, axis, insights = []) => {
  dictionary[axis._id] = axis
  axis.value = 1
  axis.totalInsights = 0
  axis.ownInsights = 0
  insights.forEach(insight => {
    if (insight.axisId === axis._id) {
      axis.ownInsights++
      axis.totalInsights++
    }
  })
  for (let child of axis.children) {
    axis.totalInsights += recursiveParseAxes(dictionary, child, insights)
  }
  return axis.totalInsights
}

// Add extra properties to original Actors and return a new dictionary with the actors
utils.parseActors = (actors, userId) => {
  let actorsDictionary = {}
  let cont = 0
  for (let actor of actors) {
    recursiveParseActors(actorsDictionary, actor, null, cont++, userId)
  }
  return actorsDictionary
}

const recursiveParseActors = (dictionary, actor, parent, cont, userId) => {
  if (parent === null) {
    actor.path = actor.slug
    actor.pathArr = []
    actor.breadcrumb = []
    actor.ancestors = []
  } else {
    actor.path = parent.path + '/' + actor.slug
    actor.pathArr = parent.pathArr.slice()
    actor.breadcrumb = parent.breadcrumb.slice()
    actor.ancestors = parent.ancestors.slice()
  }
  actor.pathArr.push(actor.slug)
  actor.breadcrumb.push(actor.name)
  actor.ancestors.push(actor._id)
  actor.color = 'color' + cont
  dictionary[actor._id] = actor

  for (let child of actor.actors) {
    recursiveParseActors(dictionary, child, actor, cont, userId)
  }
}

const recursiveParseAndMatch = (actorsName, actorList, firstLevel = false) => {
  let foundInChildren = null
  for (let actor of actorList) {
    if (actor.slug !== actorsName[0]) {
      // no match, prune branch
      continue
    }
    // discard matched name
    actorsName.shift()

    if (actor.actors) {
      foundInChildren = recursiveParseAndMatch(actorsName, actor.actors)
    }

    if (!foundInChildren) {
      const is404 = actorsName.length > 0 // there is still path we couldn't match
      const unmatchedActorName = is404 ? actorsName[0] : undefined
      foundInChildren = {selectedActor: actor, is404, unmatchedActorName}
    }
  }

  // Redirect to landscape info page in case of no 1st lvl match
  if (firstLevel && !foundInChildren) {
    foundInChildren = {
      selectedActor: {},
      is404: true,
      unmatchedActorName: actorsName[0]
    }
  }

  return foundInChildren
}

utils.parseAndMatchActor = (actorsName, actors) => {
  return recursiveParseAndMatch(actorsName, actors, true)
}

export default utils
